import React, { useEffect, useState } from "react";
import {
  Container,
  Col,
  Row,
  Card,
  CardBody,
  InputGroup,
  Input,
  Button,
  InputGroupText,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import { formatDate } from "../../../Components/Common/FormatDate";
import ReturnBill from "../component/ReturnBill";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import Flatpickr from "react-flatpickr";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";
import Select from "react-select";
import CustomerTypeService from "../../../services/OrderingServices/CustomerTypeService";
import { exportToExcel } from "../../../utilis/exportToExcel";

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ReturnBillLogs = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [pan, setPan] = useState("");
  const [touched, setTouched] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [exportLoading, setExportLoading] = useState(false);

  // Server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // Server-side filters
  const [dateRange, setDateRange] = useState([]);
  const [filters, setFilters] = useState({
    Search: "",
    CustomerTypeId: "",
    FromLastOrderDate: "",
    ToLastOrderDate: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const isInvalid = touched && !remarks?.trim();

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * perPage + index + 1,
      width: "75px",
    },
    {
      name: "Ref Sales Invoice No",
      selector: (row) => row.refSalesInvoiceNumber,
      sortable: true,
    },
    {
      name: "Return Invoice No",
      selector: (row) => row.returnInvoiceNumber,
      sortable: true,
    },
    {
      name: "Customer Pan",
      selector: (row) => row.customerPan,
      sortable: true,
    },
    {
      name: "Return Date",
      selector: (row) => formatDate(row.returnDate),
      sortable: true,
    },
    {
      name: "Discount(%)",
      selector: (row) => row.discountPercentage,
      sortable: true,
      right: true,
    },
    {
      name: "Tax Amount",
      selector: (row) => row.taxAmount,
      sortable: true,
      right: true,
    },
    {
      name: "Total Return Amount",
      selector: (row) => row.totalReturnAmount,
      cell: (row) => <NumberWithCommas number={row.totalReturnAmount} />,
      sortable: true,
      right: true,
    },
    {
      name: "Total Amount",
      selector: (row) => row.totalAmount,
      sortable: true,
      right: true,
    },
    {
      name: "Net Amount",
      selector: (row) => row.grandTotal,
      cell: (row) => <NumberWithCommas number={row.grandTotal} />,
      sortable: true,
      right: true,
    },
  ];

  const fetchReturnBill = async (
    page = currentPage,
    size = perPage,
    currentFilters = debouncedFilters,
  ) => {
    try {
      setLoading(true);
      const response = await SalesReportService.returnBillList({
        pageNumber: page,
        perPage: size,
        Search: currentFilters?.Search,
        CustomerTypeId: currentFilters.CustomerTypeId,
        FromLastOrderDate: currentFilters.FromLastOrderDate,
        ToLastOrderDate: currentFilters.ToLastOrderDate,
      });
      setData(response?.items || response || []);
      setTotalRows(response?.totalCount || 0);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching return bills:", error);
    }
  };

  // Debounce filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  // Fetch when page, perPage, or debouncedFilters change
  useEffect(() => {
    fetchReturnBill(currentPage, perPage);
  }, [currentPage, perPage, debouncedFilters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates.length === 2) {
      setDateRange(dates);
      setFilters((prev) => ({
        ...prev,
        FromLastOrderDate: formatLocalDate(dates[0]),
        ToLastOrderDate: formatLocalDate(dates[1]),
      }));
    }
  };

  // Client-side text search on already-fetched data
  const filteredData = data.filter((item) => {
    const searchContext = `${item?.refSalesInvoiceNumber || ""} ${item?.returnInvoiceNumber || ""
      } ${item?.customerPan || ""}`.toLowerCase();
    return searchContext.includes(filterText.toLowerCase());
  });

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    toggleDeleteModal();
  };

  const handleReturn = async () => {
    if (!remarks?.trim()) {
      setTouched(true);
      return;
    }
    if (selectedItem) {
      try {
        await SalesReportService.returnBill({
          refSalesInvoiceNumber: selectedItem?.invoiceNumber,
          customerPan: pan,
          totalReturnAmount: selectedItem?.grandTotal,
          remarks: remarks,
        });
        fetchReturnBill();
        setRemarks("");
        setPan("");
        toast.success("Selected bill is returned successfully");
      } catch (error) {
        toast.error("Failed to return the bill");
      } finally {
        toggleDeleteModal();
        setSelectedItem(null);
      }
    }
  };
  const [customerData, setCustomerData] = useState([]);
  const fetchCustomer = async () => {
    try {
      const response = await CustomerTypeService.getList();
      setCustomerData(response?.items);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchCustomer();
  }, []);
  const customerOptions = customerData?.map((customer) => {
    return {
      value: customer?.id,
      label: customer?.name,
    };
  });
  console.log("🚀 ~ SalesReport ~ customerOptions:", customerOptions);
  const handleCustomerChange = (selectedOption) => {
    setFilters({
      ...filters,
      CustomerTypeId: selectedOption?.value,
    });
  };
  const [exportModal, setExportModal] = useState(false);
  const toggleExportModal = () => {
    setExportModal(!exportModal);

  };

  // Handle export with date range
  const handleExportWithDateRange = async () => {
    try {
      setExportLoading(true);

      const response = await SalesReportService.returnBillList({
        pageNumber: 1, // export all from first page
        perPage: 1000, // or large number to get all data
        Search: debouncedFilters?.Search,
        CustomerTypeId: debouncedFilters?.CustomerTypeId,
        FromLastOrderDate: debouncedFilters?.FromLastOrderDate,
        ToLastOrderDate: debouncedFilters?.ToLastOrderDate,
      });

      const exportData = Array.isArray(response)
        ? response
        : response?.items || [];

      if (!exportData.length) {
        toast.error("No data found to export");
        return;
      }

      const columnMapping = {
        "S.N": (row, index) => index + 1,
        "Ref Sales Invoice No": "refSalesInvoiceNumber",
        "Return Invoice No": "returnInvoiceNumber",
        "Customer Pan": "customerPan",
        "Return Date": (row) => formatDate(row.returnDate),
        "Discount (%)": "discountPercentage",
        "Tax Amount": "taxAmount",
        "Total Return Amount": "totalReturnAmount",
        "Total Amount": "totalAmount",
        "Net Amount": "grandTotal",
      };

      exportToExcel(
        exportData,
        columnMapping,
        `ReturnBillLogs.xlsx`
      );

      toast.success("Export successful!");
      toggleExportModal();
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };
  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Return Bill Report" pageTitle="Reports" />

        <div
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
            overflow: "hidden",
          }}
        >
          {/* Main Content */}
          <div className="p-3 p-lg-4 mt-1">
            <Row className="mb-3 g-2">
              <Col md={2}>
                <div className="mb-3 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1 fw-bold text-dark">Return Bill Logs</h5>
                  </div>
                </div>
              </Col>

              {/* Search Filter */}
              <Col md={3}>
                <Input
                  type="text"
                  placeholder="Search by all "
                  value={filters.Search}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      Search: e.target.value,
                    })
                  }
                />
              </Col>
              <Col md={3}>
                <Select
                  options={customerOptions}
                  onChange={handleCustomerChange}
                  placeholder="Select Customer Type"
                  isClearable
                  menuPortalTarget={document.body} // renders dropdown at body level
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }), // high z-index
                  }}
                />
              </Col>
              <Col md={3}>
                <InputGroup>
                  <Flatpickr
                    options={{ mode: "range", dateFormat: "Y-m-d" }}
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                    className="form-control"
                  />
                  <InputGroupText style={{ cursor: "pointer" }}>
                    <i className="bx bx-calendar"></i>
                  </InputGroupText>
                </InputGroup>
              </Col>
              <Col lg={1} className="mb-2">

                <Button
                  color="success"
                  onClick={handleExportWithDateRange}
                  style={{ borderRadius: "8px" }}
                  disabled={exportLoading}
                  title="Export"
                >
                  <i className="bx bx-export"></i>

                </Button>

              </Col>
            </Row>

            <hr style={{ borderColor: "#dee2e6", marginTop: "0rem" }} />

            {/* Desktop Table */}
            <div className="d-none d-lg-block">
              <CustomDataTable
                responsive
                striped
                pagination
                fixedHeader
                highlightOnHover
                pointerOnHover
                persistTableHead
                progressPending={loading}
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationDefaultPage={currentPage}
                paginationPerPage={perPage}
                customStyles={{
                  headCells: {
                    style: {
                      fontSize: "0.92rem",
                      fontWeight: 610,
                    },
                  },
                  rows: {
                    style: {
                      fontSize: "0.9rem",
                    },
                  },
                }}
                progressComponent={
                  <div className="my-3 text-center">
                    <Triangle visible height="80" width="80" color="#5B71B9" />
                    <h5 className="mt-1">Loading...</h5>
                  </div>
                }
                columns={columns}
                data={filteredData}
                noDataComponent={
                  <div className="text-center my-5 w-100">
                    <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                    <h5 className="fw-semibold text-secondary">
                      No Data Found
                    </h5>
                    <p className="text-muted mb-0">
                      No return bills found matching your search.
                    </p>
                  </div>
                }
              />
            </div>

            {/* Mobile Cards */}
            <div className="d-block d-lg-none">
              {loading ? (
                <div className="my-3 text-center">
                  <Triangle visible height="80" width="80" color="#5B71B9" />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              ) : filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <Card
                    key={item.returnInvoiceNumber}
                    className="mb-2 shadow-sm"
                  >
                    <CardBody>
                      <div className="mb-2">
                        <strong>Invoice:</strong> {item.returnInvoiceNumber}{" "}
                        <span className="text-muted">
                          (Ref: {item.refSalesInvoiceNumber})
                        </span>
                      </div>
                      <div className="mb-2">
                        <strong>Customer Pan:</strong> {item.customerPan}
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <div>
                          <strong>Date:</strong> {formatDate(item.returnDate)}
                        </div>
                        <div>
                          <strong>Discount:</strong> {item.discountPercentage}%
                        </div>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <div>
                          <strong>Tax:</strong> Rs. {item.taxAmount}
                        </div>
                        <div className="fw-bold">
                          <strong>Total:</strong> Rs. {item.grandTotal}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))
              ) : (
                <div className="text-center py-5">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                  <h5 className="fw-semibold text-secondary mt-2">
                    No Data Found
                  </h5>
                  <p className="text-muted mb-0">No return bills found.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Return Modal */}
        <ReturnBill
          show={deleteModal}
          onDeleteClick={handleReturn}
          onCloseClick={toggleDeleteModal}
          selectedItem={selectedItem}
          setRemarks={setRemarks}
          setPan={setPan}
          pan={pan}
          remarks={remarks}
          isInvalid={isInvalid}
          setTouched={setTouched}
        />
      </div>
    </Container>
  );
};

export default ReturnBillLogs;
